import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Trophy, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { RaceJoinButton, RaceJoiners } from "@/components/RaceJoin";

/** Homepage strip: the next races the pack is travelling to. Public; guests see LOGIN TO JOIN. */
export default function UpcomingRacesStrip() {
  const [races, setRaces] = useState([]);

  useEffect(() => {
    api.get("/races").then((r) => setRaces(r.data || [])).catch(() => {});
  }, []);

  const cutoff = Date.now() - 6 * 60 * 60 * 1000;
  const upcoming = races.filter((r) => new Date(r.date).getTime() >= cutoff).slice(0, 3);
  if (upcoming.length === 0) return null;

  const replace = (u) => setRaces((rs) => rs.map((r) => (r.race_id === u.race_id ? u : r)));
  const totalIn = upcoming.reduce((n, r) => n + (r.joiners_count || 0), 0);

  return (
    <section className="border-t border-zinc-800 bg-zinc-950" data-testid="landing-races-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">§ 02 / WHERE WE&apos;RE HEADING</p>
            <div className="hidden lg:block mt-3 w-12 h-0.5 bg-orange-500" />
          </div>
          <div className="lg:col-span-9 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
                The pack&apos;s next <span className="text-orange-500">start lines.</span>
              </h2>
              <p className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mt-4">
                Races OSCR is travelling to · {totalIn} runner{totalIn === 1 ? "" : "s"} already in · Come with us
              </p>
            </div>
            <Link to="/races" data-testid="landing-races-all" className="font-display text-sm tracking-wider text-orange-500 hover:text-orange-400 inline-flex items-center gap-1 shrink-0">
              ALL RACES <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {upcoming.map((r, i) => <RaceTile key={r.race_id} race={r} index={i} onChange={replace} />)}
        </div>
      </div>
    </section>
  );
}

function RaceTile({ race, index, onChange }) {
  const d = new Date(race.date);
  const days = Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
  return (
    <article
      data-testid={`landing-race-${race.race_id}`}
      className="group hairline bg-zinc-900 hover:border-orange-600/50 hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden"
    >
      {race.image ? (
        <div className="aspect-[16/9] overflow-hidden bg-zinc-800">
          <img src={race.image} alt="" loading="lazy" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-500" />
        </div>
      ) : (
        <div className="relative aspect-[16/9] hero-bg overflow-hidden flex items-end p-5">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(234,88,12,0.35),transparent_60%)]" aria-hidden="true" />
          <span className="absolute -right-4 -top-6 font-display text-[7rem] leading-none text-white/[0.06] select-none">0{index + 1}</span>
          <p className="relative font-mono text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
            {days === 0 ? "TODAY" : `IN ${days} DAYS`}
          </p>
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <p className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
          {d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          {race.organiser && <span className="text-zinc-500"> · {race.organiser}</span>}
        </p>
        <h3 className="font-display text-2xl text-white mt-2 leading-tight group-hover:text-orange-400 transition">{race.title}</h3>
        <div className="mt-3 space-y-1.5 text-xs text-zinc-400">
          <p className="flex items-center gap-1.5"><MapPin size={12} className="text-orange-500" /> {race.location}</p>
          {race.distances && <p className="flex items-center gap-1.5"><Trophy size={12} className="text-orange-500" /> {race.distances}</p>}
        </div>
        {race.pitch && (
          <p data-testid={`landing-race-pitch-${race.race_id}`} className="mt-4 border-l-2 border-orange-600 pl-3 text-sm text-zinc-300 italic">
            {race.pitch} <span className="not-italic font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase ml-1">— Sumit</span>
          </p>
        )}
        <div className="mt-auto pt-5 space-y-3">
          <RaceJoiners race={race} max={4} />
          <div className="flex flex-wrap gap-2">
            <RaceJoinButton race={race} onChange={onChange} size="sm" />
            {race.registration_url && (
              <a
                href={race.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`landing-race-register-${race.race_id}`}
                className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 font-display tracking-wider text-xs px-3 py-2 transition"
              >
                REGISTER <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
